import { apiFetch } from './api';

const AGGREGATE_FEATURE_COLUMNS = [
    'bp_delta',
    'bp_theta',
    'bp_alpha',
    'bp_beta',
    'bp_gamma',
    'entropy',
    'fooof_offset',
    'fooof_exponent',
    'fooof_r2',
    'fooof_err',
    'fooof_n_peaks',
    'c22_00',
    'c22_01',
    'c22_02',
    'c22_03',
    'c22_04',
    'c22_05',
    'c22_06',
    'c22_07',
    'c22_08',
    'c22_09',
    'c22_10',
    'c22_11',
    'c22_12',
    'c22_13',
    'c22_14',
    'c22_15',
    'c22_16',
    'c22_17',
    'c22_18',
    'c22_19',
    'c22_20',
    'c22_21'
];


const parseCsvLine = (line) => {
    const cells = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i += 1) {
        const char = line[i];
        const next = line[i + 1];
        if (char === '"' && inQuotes && next === '"') {
            current += '"';
            i += 1;
        } else if (char === '"') {
            inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
            cells.push(current);
            current = '';
        } else {
            current += char;
        }
    }

    cells.push(current);
    return cells;
};

const parseCsv = (text) => {
    const lines = text
        .replace(/^\uFEFF/, '')
        .split(/\r?\n/)
        .filter(line => line.trim().length > 0);
    if (lines.length === 0) {
        return [];
    }

    const headers = parseCsvLine(lines[0]).map(header => header.trim());
    return lines.slice(1).map(line => {
        const values = parseCsvLine(line);
        return headers.reduce((row, header, index) => {
            row[header] = values[index] ?? '';
            return row;
        }, {});
    });
};

const asNumber = (value) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const canonicalRegionKey = (value) => (
    String(value || '')
        .normalize('NFKD')
        .replace(/^\uFEFF/, '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '')
);

const normalizeRegionName = (value) => {
    if (!value) {
        return 'Unknown';
    }
    return String(value).trim() || 'Unknown';
};

const addRegionAlias = (aliasMap, alias, canonicalRegion) => {
    const key = canonicalRegionKey(alias);
    if (key && canonicalRegion) {
        aliasMap.set(key, canonicalRegion);
    }
};

const loadAtlasRegionAliases = async () => {
    const aliasMap = new Map();
    try {
        const response = await fetch('/atlas/brain_region_map.csv');
        if (!response.ok) {
            return aliasMap;
        }
        const rows = parseCsv(await response.text());
        rows.forEach(row => {
            const roi = normalizeRegionName(row.roi);
            if (!roi || roi === 'Unknown') {
                return;
            }
            addRegionAlias(aliasMap, roi, roi);
            addRegionAlias(aliasMap, row.region_name, roi);
            addRegionAlias(aliasMap, row.abbreviation, roi);
        });
    } catch (error) {
        console.warn('Unable to load atlas region aliases for local upload:', error);
    }
    return aliasMap;
};

const registerModelAlias = (modelMap, aliasMap, regionName, feature, model) => {
    const normalizedRegion = normalizeRegionName(regionName);
    const aliasRegion = aliasMap.get(canonicalRegionKey(normalizedRegion));
    const candidates = [
        normalizedRegion,
        aliasRegion
    ].filter(Boolean);

    candidates.forEach(candidate => {
        modelMap.set(`${canonicalRegionKey(candidate)}::${feature}`, model);
    });
};

const getModelForRegion = (modelMap, regionName, featureId) => {
    const normalizedRegion = normalizeRegionName(regionName);
    const aliasMap = modelMap.regionAliases || new Map();
    const candidates = [
        normalizedRegion,
        aliasMap.get(canonicalRegionKey(normalizedRegion))
    ].filter(Boolean);

    for (const candidate of candidates) {
        const model = modelMap.get(`${canonicalRegionKey(candidate)}::${featureId}`);
        if (model) {
            return model;
        }
    }
    return null;
};

const resolveModelRegion = (modelMap, regionName) => {
    const normalizedRegion = normalizeRegionName(regionName);
    return modelMap.regionAliases?.get(canonicalRegionKey(normalizedRegion)) || normalizedRegion;
};

export const getBundleFeatureIds = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) {
        return [];
    }

    const columns = new Set(Object.keys(rows[0]));
    return AGGREGATE_FEATURE_COLUMNS.filter(featureId => columns.has(featureId));
};

export const parseLocalFeatureBundle = async (file) => {
    const filename = file?.name || 'local_features.csv';
    const lowerName = filename.toLowerCase();
    let manifest = null;
    let csvText = '';

    if (lowerName.endsWith('.zip')) {
        const JSZip = (await import('jszip')).default;
        const zip = await JSZip.loadAsync(file);
        const manifestFile = zip.file('manifest.json');
        if (!manifestFile) {
            throw new Error('The local feature zip is missing manifest.json.');
        }

        manifest = JSON.parse(await manifestFile.async('string'));
        const featurePath = manifest?.files?.features_csv || 'features/all_8_cohorts_unharmonized_uni_feb26.csv';
        const featureFile = zip.file(featurePath);
        if (!featureFile) {
            throw new Error(`The local feature zip is missing ${featurePath}.`);
        }
        csvText = await featureFile.async('string');
    } else if (lowerName.endsWith('.csv')) {
        csvText = await file.text();
        manifest = {
            site: 'Local',
            source_filename: filename,
            files: {
                features_csv: filename
            }
        };
    } else {
        throw new Error('Upload the Neuronova local feature .zip bundle or an aggregate feature .csv file.');
    }

    const rows = parseCsv(csvText);
    if (rows.length === 0) {
        throw new Error('The uploaded feature table has no rows.');
    }

    const subjects = [...new Set(rows.map(row => row.subject_id || row.patient_id).filter(Boolean))];
    if (subjects.length === 0) {
        throw new Error('The feature table must contain a subject_id or patient_id column.');
    }

    const site = manifest?.site || rows[0]?.site || rows[0]?.dataset_name || 'Local';
    return {
        id: `${site}-${filename}-${Date.now()}`,
        filename,
        manifest,
        rows,
        site,
        subjects,
        featureIds: getBundleFeatureIds(rows)
    };
};

export const fetchNormativeModelsForFeatures = async (featureIds, montageType) => {
    const uniqueFeatures = [...new Set(featureIds)].filter(Boolean);
    const regionAliases = await loadAtlasRegionAliases();
    const modelResponses = await Promise.all(uniqueFeatures.map(async (featureId) => {
        const response = await apiFetch(
            `/api/normative-models?feature=${encodeURIComponent(featureId)}&montage=${encodeURIComponent(montageType)}`,
            { timeoutMs: 60000 }
        );
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
            throw new Error(`No normative model JSON available for ${featureId}.`);
        }
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.message || data.error || `Unable to fetch model for ${featureId}.`);
        }
        return Array.isArray(data) ? data : [];
    }));

    const modelMap = new Map();
    modelMap.regionAliases = regionAliases;
    modelResponses.flat().forEach(model => {
        const region = normalizeRegionName(model.region_name || model.region_id);
        const feature = model.feature_name;
        if (region && feature) {
            registerModelAlias(modelMap, regionAliases, region, feature, model);
        }
    });
    return modelMap;
};

export const buildLocalPatientZScores = ({ bundle, selectedFeatures, normativeModelMap }) => {
    const featuresToUse = [...new Set(selectedFeatures)].filter(Boolean);
    const patients = new Map();

    bundle.rows.forEach((row, rowIndex) => {
        const patientId = row.subject_id || row.patient_id;
        if (!patientId) {
            return;
        }

        const uploadedRegionName = normalizeRegionName(row.roi || row.region || row.region_name);
        const regionName = resolveModelRegion(normativeModelMap, uploadedRegionName);
        const channelName = row.channel || row.electrode_id || row.contact || `channel_${rowIndex + 1}`;
        const x = asNumber(row.x ?? row.mm_x ?? row.surfmm_x);
        const y = asNumber(row.y ?? row.mm_y ?? row.surfmm_y);
        const z = asNumber(row.z ?? row.mm_z ?? row.surfmm_z);
        const zScores = {};

        featuresToUse.forEach(featureId => {
            const rawValue = asNumber(row[featureId]);
            const model = getModelForRegion(normativeModelMap, uploadedRegionName, featureId);
            const std = asNumber(model?.std ?? model?.stddev ?? model?.sd);
            const mean = asNumber(model?.mean);
            if (rawValue === null || mean === null || std === null || std === 0) {
                return;
            }
            zScores[featureId] = {
                z_score: (rawValue - mean) / std,
                raw_value: rawValue,
                normative_mean: mean,
                normative_std: std,
                uploaded_region: uploadedRegionName,
                source: 'local_browser_bundle'
            };
        });

        if (Object.keys(zScores).length === 0) {
            return;
        }

        if (!patients.has(patientId)) {
            patients.set(patientId, {
                patient_id: patientId,
                source: 'local_browser_bundle',
                local_federated: true,
                site: bundle.site,
                regions: new Map()
            });
        }

        const patient = patients.get(patientId);
        if (!patient.regions.has(regionName)) {
            patient.regions.set(regionName, {
                region_name: regionName,
                channels: []
            });
        }

        patient.regions.get(regionName).channels.push({
            channel: channelName,
            roi: regionName,
            coordinates: {
                x,
                y,
                z,
                mm_x: x,
                mm_y: y,
                mm_z: z,
                surfmm_x: x,
                surfmm_y: y,
                surfmm_z: z
            },
            z_scores: zScores
        });
    });

    return Array.from(patients.values()).map(patient => ({
        ...patient,
        regions: Array.from(patient.regions.values())
    }));
};
